<template>
  <Dialog
    :visible="visible"
    modal
    appendTo="body"
    :dismissable-mask="!creatingSizing"
    :closable="!creatingSizing"
    :close-on-escape="!creatingSizing"
    header="AI Suggested Designs"
    class="ai-suggestion-dialog"
    :style="{ width: 'min(72vw, 1320px)' }"
    :breakpoints="{ '960px': '90vw', '640px': '95vw' }"
    data-cy="ai-suggestion-modal"
    @update:visible="emit('update:visible', $event)"
    @show="onDialogShow"
    @hide="onDialogHide"
  >
    <div v-if="targetLiquidRateStbd" class="target-banner">
      <icon :icon="['fas', 'bullseye']" class="mr-2" />
      <span
        >Target Production:
        <strong
          >{{ Number(targetLiquidRateStbd).toLocaleString() }} STB/D</strong
        ></span
      >
    </div>

    <div v-if="store.error" class="error-banner" data-cy="ai-error">
      <icon :icon="['fas', 'triangle-exclamation']" class="mr-2" />
      <span>{{ store.error }}</span>
      <Button
        label="Retry"
        class="p-button-sm p-button-text"
        data-cy="retry-ai-suggestions"
        :disabled="store.loading"
        @click="loadEquipmentSuggestions(true)"
      />
    </div>
    <div v-if="store.loading" class="loading-state" data-cy="ai-loading">
      <icon :icon="['far', 'spinner-third']" class="spin mr-2" />
      <div role="status" aria-live="polite" data-cy="ai-progress">
        <div>{{ store.progress?.stage || 'Loading AI suggestions...' }}</div>
        <small v-if="store.progress">
          <span v-if="store.progress.candidate">Candidate {{ store.progress.candidate }} · </span>
          <span v-if="store.progress.verified != null">{{ store.progress.verified }} verified · </span>
          {{ Math.floor((store.progress.elapsedSeconds || 0) / 60) }}m {{ (store.progress.elapsedSeconds || 0) % 60 }}s elapsed
        </small>
      </div>
    </div>

    <div
      v-if="warningMeta.length"
      class="option-warnings"
      data-cy="ai-warnings"
    >
      <h4 class="warnings-title">Warnings</h4>
      <ul class="warnings-list">
        <li v-for="wm in warningMeta" :key="wm.code" :title="wm.code">
          <strong>{{ wm.label }}:</strong> {{ wm.description }}
        </li>
      </ul>
    </div>

    <p class="modal-description">
      Select a calculation-checked candidate based on your design priorities:
    </p>
    <div v-if="store.equipmentOptions" class="recommendation-cache-controls">
      <span v-if="store.lastUpdated">
        {{ store.usingCachedResults ? 'Cached results' : 'Last run' }}:
        {{ new Date(store.lastUpdated).toLocaleString() }}
      </span>
    </div>
    <div v-if="selectedEquipmentOption?.incomplete" class="warning-banner" role="status">
      <div>
        <strong>Incomplete design — equipment needs to be added</strong>
        <p>You can Apply or Create this draft, then finish it in the sizing editor. Performance metrics are withheld until missing equipment is supplied and the design is recalculated.</p>
        <ul>
          <li v-for="item in selectedEquipmentOption.completionRequirements" :key="item.code">
            {{ item.equipmentType }}: {{ item.message }} {{ item.action }}
          </li>
        </ul>
      </div>
    </div>
    <p v-if="unavailableReason || applyError" class="error-banner" role="alert" data-cy="ai-apply-error">
      {{ unavailableReason || applyError }}
    </p>
    <p v-if="applyRecommendation" class="modal-description">
      Apply to Sizing updates the current editor. Review the design, then Save to
      keep the changes or Cancel to discard them.
    </p>
    <p v-if="createSizing" class="modal-description">
      Create a new sizing with this recommendation and the source well and
      operating inputs. The new sizing will open for engineering review.
    </p>
    <p
      v-if="(createSizing || applyRecommendation) && selectedEquipmentOption && !selectedEquipmentOption.sizingDraft"
      class="error-banner"
    >
      This recommendation does not include a sizing payload. Generate a new
      recommendation using the updated service.
    </p>

    <TabView
      v-model:active-index="activeTabIndex"
      class="ai-options-tabs"
      data-cy="ai-options-tabs"
    >
      <TabPanel>
        <template #header>
          <div class="tab-header" data-cy="ai-performance-tab">
            <icon :icon="['fas', 'chart-line']" class="mr-2" />
            <span>Performance Focus</span
            ><span
              v-if="store.appliedPriority === 'performance'"
              class="applied-badge"
              >Selected</span
            >
          </div>
        </template>
        <div v-if="performanceSuggestion" class="tab-content">
          <div
            v-if="store.equipmentOptions?.performance?.warnings?.length"
            class="option-warnings"
          >
            <h4 class="warnings-title">Warnings</h4>
            <ul class="warnings-list">
              <li
                v-for="wm in performanceWarningMeta"
                :key="wm.code"
                :title="wm.code"
              >
                <strong>{{ wm.label }}:</strong> {{ wm.description }}
              </li>
            </ul>
          </div>
          <div
            v-if="store.equipmentOptions?.performance"
            class="equipment-details"
          >
            <AIDesignOptionPanel v-if="activeTabIndex === 0" :option="store.equipmentOptions.performance" :scenario="previewScenario" :sizing="sizingFormOpt?.data.value" :issues="store.equipmentIssues" />
          </div>
          <!-- Performance-specific AI Narrative -->
          <div
            v-if="enableNarrative"
            class="tab-narrative"
            data-cy="ai-enhancement-wrapper-performance"
          >
            <EnhancementNarrative priority="performance" />
            <div
              v-if="store.candidateNotes?.performance"
              class="candidate-notes"
              style="margin-top: 0.5rem"
            >
              <h5
                style="
                  margin: 0 0 0.5rem 0;
                  font-size: 0.8rem;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                "
              >
                Candidate Notes
              </h5>
              <ul style="margin: 0; padding-left: 1rem; font-size: 0.75rem">
                <li v-for="(v, k) in store.candidateNotes.performance" :key="k">
                  {{ k }}: {{ formatCandidateNote(v) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else class="tab-content">No performance suggestion loaded.</div>
      </TabPanel>

      <TabPanel>
        <template #header>
          <div class="tab-header" data-cy="ai-runlife-tab">
            <icon :icon="['fas', 'heartbeat']" class="mr-2" />
            <span>Run-Life Focus</span
            ><span
              v-if="store.appliedPriority === 'runlife'"
              class="applied-badge"
              >Selected</span
            >
          </div>
        </template>
        <div v-if="runlifeSuggestion" class="tab-content">
          <div
            v-if="store.equipmentOptions?.runlife?.warnings?.length"
            class="option-warnings"
          >
            <h4 class="warnings-title">Warnings</h4>
            <ul class="warnings-list">
              <li
                v-for="wm in runlifeWarningMeta"
                :key="wm.code"
                :title="wm.code"
              >
                <strong>{{ wm.label }}:</strong> {{ wm.description }}
              </li>
            </ul>
          </div>
          <div v-if="store.equipmentOptions?.runlife" class="equipment-details">
            <AIDesignOptionPanel v-if="activeTabIndex === 1" :option="store.equipmentOptions.runlife" :scenario="previewScenario" :sizing="sizingFormOpt?.data.value" :issues="store.equipmentIssues" />
          </div>
          <!-- Runlife-specific AI Narrative -->
          <div
            v-if="enableNarrative"
            class="tab-narrative"
            data-cy="ai-enhancement-wrapper-runlife"
          >
            <EnhancementNarrative priority="runlife" />
            <div
              v-if="store.candidateNotes?.runlife"
              class="candidate-notes"
              style="margin-top: 0.5rem"
            >
              <h5
                style="
                  margin: 0 0 0.5rem 0;
                  font-size: 0.8rem;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                "
              >
                Candidate Notes
              </h5>
              <ul style="margin: 0; padding-left: 1rem; font-size: 0.75rem">
                <li v-for="(v, k) in store.candidateNotes.runlife" :key="k">
                  {{ k }}: {{ formatCandidateNote(v) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else class="tab-content">No run life suggestion loaded.</div>
      </TabPanel>

      <TabPanel>
        <template #header>
          <div class="tab-header" data-cy="ai-cost-tab">
            <icon :icon="['fas', 'file-invoice-dollar']" class="mr-2" />
            <span>Cost Focus</span
            ><span v-if="store.appliedPriority === 'cost'" class="applied-badge"
              >Selected</span
            >
          </div>
        </template>
        <div v-if="costSuggestion" class="tab-content">
          <div
            v-if="store.equipmentOptions?.cost?.warnings?.length"
            class="option-warnings"
          >
            <h4 class="warnings-title">Warnings</h4>
            <ul class="warnings-list">
              <li v-for="wm in costWarningMeta" :key="wm.code" :title="wm.code">
                <strong>{{ wm.label }}:</strong> {{ wm.description }}
              </li>
            </ul>
          </div>
          <div v-if="store.equipmentOptions?.cost" class="equipment-details">
            <AIDesignOptionPanel v-if="activeTabIndex === 2" :option="store.equipmentOptions.cost" :scenario="previewScenario" :sizing="sizingFormOpt?.data.value" :issues="store.equipmentIssues" />
          </div>
          <!-- Cost-specific AI Narrative -->
          <div
            v-if="enableNarrative"
            class="tab-narrative"
            data-cy="ai-enhancement-wrapper-cost"
          >
            <EnhancementNarrative priority="cost" />
            <div
              v-if="store.candidateNotes?.cost"
              class="candidate-notes"
              style="margin-top: 0.5rem"
            >
              <h5
                style="
                  margin: 0 0 0.5rem 0;
                  font-size: 0.8rem;
                  text-transform: uppercase;
                  letter-spacing: 0.5px;
                "
              >
                Candidate Notes
              </h5>
              <ul style="margin: 0; padding-left: 1rem; font-size: 0.75rem">
                <li v-for="(v, k) in store.candidateNotes.cost" :key="k">
                  {{ k }}: {{ formatCandidateNote(v) }}
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-else class="tab-content">No cost suggestion loaded.</div>
      </TabPanel>
    </TabView>

    <template #footer>
      <p v-if="applyError" role="alert" class="error-banner" data-cy="ai-create-error">
        {{ applyError }}
      </p>
      <div class="ai-footer-actions">
        <div class="ai-footer-run-control">
          <Button v-if="store.loading" label="Stop calculation"
            class="p-button-danger p-button-outlined" data-cy="stop-ai-calculation"
            :disabled="!store.activeJobId" @click="stopCalculation" />
          <Button v-else-if="store.equipmentOptions" label="Rerun"
            icon="pi pi-refresh" class="p-button-outlined" data-cy="rerun-ai-suggestions"
            :disabled="creatingSizing || !!unavailableReason"
            @click="loadEquipmentSuggestions(true)" />
        </div>
        <div class="ai-footer-design-actions">
      <Button
        label="Cancel"
        class="p-button-text"
        data-cy="cancel-ai-modal"
        :disabled="creatingSizing"
        @click="handleCancel"
      />
      <Button
        v-if="applyRecommendation || !createSizing"
        :label="applyRecommendation ? 'Apply to Sizing' : 'Select Recommendation'"
        class="p-button-info"
        data-cy="apply-ai-selection"
        :loading="pendingAction === 'apply'"
        :disabled="actionDisabled"
        @click="handleApply('apply')"
      >
        <template #icon>
          <icon :icon="['fas', 'sparkles']" class="mr-2" />
        </template>
      </Button>
      <Button
        v-if="createSizing"
        label="Create Sizing"
        class="p-button-success"
        data-cy="create-ai-sizing"
        :loading="pendingAction === 'create'"
        :disabled="actionDisabled"
        @click="handleApply('create')"
      >
        <template #icon>
          <icon :icon="['fas', 'sparkles']" class="mr-2" />
        </template>
      </Button>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import {
  ref,
  watch,
  computed,
  toRefs,
  onMounted,
  inject,
} from "vue";
import { useAISuggestionsStore } from "../store/aiSuggestions";
import Dialog from "primevue/dialog";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Button from "primevue/button";
import AIDesignOptionPanel from "@/components/ai/AIDesignOptionPanel.vue";
import useUserInfo from "@/composables/auth/useUserInfo";
import { scopeSizingJobs, readSizingJobs } from "@/libs/ai/activeSizingJobs";
import EnhancementNarrative from "@/components/ai/EnhancementNarrative.vue";
import { injectStrict } from "@/utils/utils";
import {
  scenarioCalculationsKey,
  scenarioFormKey,
  sizingFormKey,
  wellPropertiesFormKey,
  parametersFormKey,
  scenarioKey,
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { mapWarnings } from "@/libs/ai/warningLabels";
import type { AIOptimizationPriority, AIEquipmentSuggestionOption } from "@/libs/api/aiEquipmentSuggestions";


const props = defineProps<{
  visible: boolean;
  targetLiquidRateStbd?: number;
  createSizing?: (option: AIEquipmentSuggestionOption) => Promise<void>;
  applyRecommendation?: (option: AIEquipmentSuggestionOption) => void | Promise<void>;
  unavailableReason?: string;
}>();

const emit = defineEmits<{
  (e: "update:visible", value: boolean): void;
  (e: "selection-applied", option: string): void;
}>();

const store = useAISuggestionsStore();
const user = useUserInfo();
scopeSizingJobs(String(user.id || user.identifier || 'signed-out'));
store.durableJobs = readSizingJobs();
async function stopCalculation() {
  try { await store.cancelEquipmentJob(); }
  catch (error) { store.error = error instanceof Error ? error.message : String(error); }
}
const applyError = ref("");
const pendingAction = ref<"apply" | "create" | null>(null);
const creatingSizing = computed(() => pendingAction.value !== null);
const performanceSuggestion = computed(() => {
  return store.equipmentOptions?.performance || null;
});
const runlifeSuggestion = computed(() => {
  return store.equipmentOptions?.runlife || null;
});
const costSuggestion = computed(() => {
  return store.equipmentOptions?.cost || null;
});
const warningMeta = computed(() => {
  const optionWarnings = new Set(
    Object.values(store.equipmentOptions || {}).flatMap(
      (option) => option?.warnings || [],
    ),
  );
  return mapWarnings(
    store.warnings.filter((code) => !optionWarnings.has(code)),
  );
});
const performanceWarningMeta = computed(() =>
  mapWarnings(store.equipmentOptions?.performance?.warnings),
);
const runlifeWarningMeta = computed(() =>
  mapWarnings(store.equipmentOptions?.runlife?.warnings),
);
const costWarningMeta = computed(() =>
  mapWarnings(store.equipmentOptions?.cost?.warnings),
);
const activeTabIndex = ref(0);
const optionNames: AIOptimizationPriority[] = [
  "performance",
  "runlife",
  "cost",
];
const selectedEquipmentOption = computed(
  () => store.equipmentOptions?.[optionNames[activeTabIndex.value]] || null,
);
const actionDisabled = computed(() =>
  creatingSizing.value || store.loading || !selectedEquipmentOption.value ||
  !!props.unavailableReason ||
  (!!(props.createSizing || props.applyRecommendation) && !selectedEquipmentOption.value?.sizingDraft)
);

// Inject scenario calculations for metrics derivation
const scenarioCalculations = injectStrict(scenarioCalculationsKey);
// Optional forms (may be absent in lightweight contexts like unit tests)
const scenarioFormOpt = inject(scenarioFormKey, null);
const sourceScenario = inject(scenarioKey, null);
const previewScenario = computed(() => sourceScenario?.value);
const sizingFormOpt = inject(sizingFormKey, null);
const wellPropsFormOpt = inject(wellPropertiesFormKey, null);
const paramsFormOpt = inject(parametersFormKey, null);

function handleCancel() {
  if (creatingSizing.value) return;
  console.debug("[AI_MODAL] Cancel clicked");
  emit("update:visible", false);
}

async function handleApply(action: "apply" | "create") {
  if (actionDisabled.value) return;
  const selected = optionNames[activeTabIndex.value];
  console.debug("[AI_MODAL] Apply clicked; selected option:", selected);
  const option = store.equipmentOptions?.[selected];
  if (!option) return;
  if (props.unavailableReason) return;
  applyError.value = "";
  pendingAction.value = action;
  try {
    if (action === "create" && props.createSizing) {
      await props.createSizing(option);
      emit("selection-applied", selected);
      emit("update:visible", false);
      return;
    }
    if (props.applyRecommendation) await props.applyRecommendation(option);
    const applied = store.applySuggestion(selected);
    if (!applied) return;
    emit("selection-applied", selected);
    emit("update:visible", false);
  } catch (error) {
    applyError.value = error instanceof Error ? error.message : String(error);
  } finally {
    pendingAction.value = null;
  }
}

function onDialogShow() {
  console.debug("[AI_MODAL] PrimeVue Dialog show event fired");
}
function onDialogHide() {
  console.debug("[AI_MODAL] PrimeVue Dialog hide event fired");
  // Keep the result (or in-flight request) for reopening this scenario.
}

function formatCandidateNote(val: unknown) {
  if (val == null) return "—";
  if (typeof val === "object") {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return String(val);
}

onMounted(() => {
  console.debug("[AI_MODAL] component mounted");
});

const { visible, targetLiquidRateStbd } = toRefs(props);
const enableNarrative = import.meta.env.VITE_ENABLE_LLM_NARRATIVE !== "false";

async function loadEquipmentSuggestions(force = false) {
  applyError.value = "";
  const scenarioId = scenarioFormOpt?.data.value.id;
  if (props.unavailableReason && !(scenarioId && store.durableJobs[scenarioId])) return;
  const sizingId = sizingFormOpt?.data.value.id;
  const wellProperties = wellPropsFormOpt?.data.value || {};
  const parameters: Record<string, unknown> = {
    ...(paramsFormOpt?.data.value || {}),
  };
  const primaryPump = scenarioCalculations.primaryPump.value;
  const individualPumps =
    scenarioCalculations.pumpCalculations.individual.value;
  const primaryPumpCalcs = primaryPump ? individualPumps[primaryPump.id] : null;

  if (primaryPumpCalcs) {
    parameters.calculatedTotalDynamicHeadFt =
      primaryPumpCalcs.totalDynamicHeadFt;
    parameters.calculatedRequiredStages = primaryPumpCalcs.requiredStages;
  }

  const optimizationPriority = optionNames.includes(
    store.priority as AIOptimizationPriority,
  )
    ? (store.priority as AIOptimizationPriority)
    : "performance";
  await store.loadEquipmentSuggestions({
    scenarioId,
    sizingId,
    wellProperties,
    parameters,
    optimizationPriority,
  }, {
    force,
    inputIdentity: {
      scenario: sourceScenario?.value || scenarioFormOpt?.data.value,
      sizingRevision: sizingFormOpt?.data.value.winning_revision_id,
    },
  });
}

// When the modal opens, load calculation-verified equipment suggestions.
watch(
  () => visible.value,
  async (val) => {
    console.debug("[AI_MODAL] prop visible changed:", val);
    if (val) {
      await loadEquipmentSuggestions();
    }
  },
);

// Sync active tab with store priority (optional for future logic)
watch(activeTabIndex, (idx) => {
  const p = optionNames[idx];
  store.priority = p;
  console.debug("[AI_MODAL] store.priority set to", p);
});
</script>

<style lang="scss" scoped>
.recommendation-cache-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
  color: var(--text-color-secondary);
}
.target-banner {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: var(--blue-50);
  border: 1px solid var(--blue-200);
  border-radius: 6px;
  color: var(--blue-700);
  font-size: 0.95rem;
  strong {
    color: var(--blue-900);
    font-size: 1.05rem;
  }
}
.error-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  margin-bottom: 0.75rem;
  background: var(--red-50);
  border: 1px solid var(--red-200);
  border-radius: 6px;
  color: var(--red-700);
  font-size: 0.85rem;
}
.warning-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  border-radius: 6px;
  color: var(--yellow-800);
  font-size: 0.8rem;
  ul {
    margin: 0;
    padding-left: 1rem;
  }
}
.loading-state {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  background: var(--surface-100);
  border: 1px solid var(--surface-border);
  border-radius: 4px;
  margin-bottom: 1rem;
  color: var(--text-color-secondary);
  .spin {
    animation: spin 1s linear infinite;
  }
}
@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
.modal-description {
  margin-bottom: 1.5rem;
  color: var(--text-color);
  font-size: 0.95rem;
}
.tab-header {
  display: flex;
  align-items: center;
  font-size: 0.9rem;
  gap: 0.4rem;
  .applied-badge {
    background: var(--green-100);
    color: var(--green-700);
    font-size: 0.65rem;
    padding: 0.15rem 0.4rem;
    border-radius: 4px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border: 1px solid var(--green-300);
  }
}
.ai-options-tabs :deep(.p-tabview-header.p-highlight .p-tabview-nav-link),
.ai-options-tabs :deep(.p-tabview-nav-link[aria-selected="true"]) {
  color: var(--text-color) !important;
}
.ai-options-tabs :deep(.p-tabview-header.p-highlight .tab-header > *),
.ai-options-tabs
  :deep(.p-tabview-nav-link[aria-selected="true"] .tab-header > *) {
  color: var(--text-color) !important;
}
.tab-content {
  display: flex;
  flex-direction: column;
  > .equipment-details { order: -1; }
  padding: 1rem 0;
}
.ai-footer-actions { display: flex; justify-content: space-between; align-items: center; gap: 1rem; width: 100%; flex-wrap: wrap; }
.ai-footer-design-actions { display: flex; justify-content: flex-end; gap: 0.5rem; margin-left: auto; flex-wrap: wrap; }
.option-description {
  margin-bottom: 1.5rem;
  color: var(--text-color-secondary);
  line-height: 1.6;
}
.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.metric-card {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  border: 1px solid var(--surface-border);
  border-radius: 6px;
  background: var(--surface-50);
  .metric-label {
    font-size: 0.75rem;
    color: var(--text-color-secondary);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 0.5rem;
  }
  .metric-value {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--primary-color);
  }
}
.option-warnings {
  padding: 0.75rem 1rem;
  background: var(--yellow-50);
  border: 1px solid var(--yellow-200);
  border-radius: 6px;
  margin-bottom: 1rem;
  .warnings-title {
    margin: 0 0 0.5rem 0;
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--yellow-800);
  }
  .warnings-list {
    margin: 0;
    padding-left: 1rem;
    font-size: 0.75rem;
    color: var(--yellow-900);
    li {
      line-height: 1.3;
    }
  }
}
.tab-narrative {
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 2px solid var(--surface-border);
}
.equipment-summary {
  padding: 1rem;
  background: var(--surface-100);
  border-radius: 6px;
  border-left: 3px solid var(--green-500);
  h4 {
    margin: 0 0 0.75rem 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--text-color);
  }
  .pump-config {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    .pump-item {
      color: var(--text-color);
      font-size: 0.9rem;
      strong {
        color: var(--text-color-secondary);
        font-weight: 500;
      }
    }
    .pump-details {
      display: flex;
      gap: 1rem;
      flex-wrap: wrap;
      padding-left: 1rem;
      margin-top: 0.25rem;
      span {
        font-size: 0.85rem;
        color: var(--text-color-secondary);
        font-family: var(--monospace-family);
      }
    }
    .housing-breakdown {
      margin-top: 1rem;
      padding-top: 0.75rem;
      border-top: 1px solid var(--surface-border);
      .housing-label {
        font-size: 0.85rem;
        font-weight: 600;
        color: var(--text-color);
        margin-bottom: 0.5rem;
      }
      .housing-item {
        display: flex;
        flex-direction: column;
        padding: 0.5rem 0.75rem;
        margin-bottom: 0.5rem;
        background: var(--surface-0);
        border: 1px solid var(--surface-border);
        border-radius: 4px;
        .housing-name {
          font-size: 0.85rem;
          color: var(--text-color);
          margin-bottom: 0.25rem;
        }
        .housing-specs {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          padding-left: 1rem;
          span {
            font-size: 0.8rem;
            color: var(--text-color-secondary);
            font-family: var(--monospace-family);
          }
        }
      }
    }
  }
}
:deep(.p-dialog-mask) {
  background: rgba(0, 0, 0, 0.55);
}
/* Responsive adjustments */
@media (max-width: 768px) {
  :deep(.p-dialog) {
    width: 95vw !important;
  }
  .metrics-grid {
    grid-template-columns: 1fr;
  }
}
</style>

<style lang="scss">
.ai-suggestion-dialog .p-dialog-footer {
  padding-top: 1rem;
  border-top: 1px solid var(--surface-border);
}

.p-dialog-mask {
  z-index: 1100 !important;
}
.p-dialog {
  z-index: 1110 !important;
}
</style>
